import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
  cta: string;
  popular: boolean;
  highlight: boolean;
}

interface PricingCardProps {
  plan: Plan;
  isAnnual: boolean;
  appBaseUrl: string;
}

export const PricingCard: React.FC<PricingCardProps> = ({ plan, isAnnual, appBaseUrl }) => {
  const textColorPrimary = plan.highlight ? 'text-white' : 'text-slate-950';
  const textColorSecondary = plan.highlight ? 'text-slate-300' : 'text-slate-600';
  const textColorMuted = plan.highlight ? 'text-slate-400' : 'text-slate-500';
  const featureIconBg = plan.highlight ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900';
  const featureText = plan.highlight ? 'text-slate-200' : 'text-slate-700';
  const dividerColor = plan.highlight ? 'bg-slate-700' : 'bg-slate-200';

  const signUpHref = appBaseUrl ? `${appBaseUrl}/auth?mode=signup&plan=${plan.id}` : `/auth?mode=signup&plan=${plan.id}`;

  return (
    <div className={`group relative ${plan.popular ? 'z-10 md:-mt-4' : ''}`}>
      {plan.popular && (
        <div className="absolute -top-4 left-0 right-0 z-20 flex justify-center">
          <Badge className="rounded-full bg-emerald-600 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg">
            Most Popular
          </Badge>
        </div>
      )}

      <Card
        className={`flex h-full flex-col border transition-all duration-300 hover:shadow-xl ${
          plan.highlight
            ? 'scale-105 border-slate-900 bg-slate-900 shadow-2xl md:scale-100 md:hover:scale-105'
            : 'border-slate-200 bg-white hover:border-slate-300'
        }`}
      >
        <CardHeader className="p-10 pb-4 text-center">
          <CardTitle className={`text-3xl font-bold ${textColorPrimary}`}>{plan.name}</CardTitle>
          <p className={`mt-4 min-h-[3rem] text-base leading-relaxed ${textColorSecondary}`}>{plan.description}</p>
        </CardHeader>

        <CardContent className="flex flex-grow flex-col p-10 pt-0">
          <div className="mb-8 flex items-baseline justify-center gap-1">
            <span className={`text-5xl font-extrabold ${textColorPrimary}`}>${isAnnual ? Math.round(plan.annualPrice / 12) : plan.monthlyPrice}</span>
            <span className={`text-lg ${textColorMuted}`}>/mo</span>
          </div>

          {isAnnual && plan.annualPrice > 0 && (
            <div className="-mt-6 mb-6 flex justify-center">
              <Badge
                variant="secondary"
                className={`px-3 py-1 ${plan.highlight ? 'border-slate-700 bg-slate-800 text-slate-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}
              >
                Billed ${plan.annualPrice} yearly
              </Badge>
            </div>
          )}

          <div className={`mb-8 h-px w-full ${dividerColor}`} />

          <ul className="mb-10 flex-grow space-y-5">
            {plan.features.map((feature, i) => (
              <li key={i} className={`flex items-start gap-4 text-base ${featureText}`}>
                <div className={`mt-0.5 shrink-0 rounded-full p-0.5 ${featureIconBg}`}>
                  <Check size={14} strokeWidth={3} />
                </div>
                <span className="leading-tight">{feature}</span>
              </li>
            ))}
          </ul>

          <Button
            size="lg"
            className={`h-14 w-full rounded-xl text-lg font-semibold shadow-lg transition-all active:scale-95 ${
              plan.highlight ? 'bg-emerald-500 text-white hover:bg-emerald-400' : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
            asChild
          >
            <a href={signUpHref}>{plan.cta}</a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
